import { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Space } from 'antd';
import { Customer, CustomerContact } from 'store/customers/customer';
import { FormikProvider, setNestedObjectValues, useFormik, yupToFormErrors } from 'formik';
import Button from 'components/new-ui/base/button/Button';
import useSession from 'hooks/useSession';
import useLoadingButton from 'hooks/useLoadingButton';
import useRequest from 'hooks/useRequest';
import { createCustomer, editCustomer } from 'store/customers/client';
import { ErrorResponse } from 'core/http/response';
import { objectIsEmpty, removeEmptyStringFromObject } from 'core/utils/objects';
import { extractPhoneNumber } from 'utils/phoneNumber';
import { Address } from 'store/individuals/individual';
import { Steps } from '../styles';
import {
    dutyVatBillingCustomerSchema,
    generalCustomerSchema,
    getCustomerSchema,
} from 'views/customers/validationSchema';
import { combinePhone } from 'views/customers/utils';
import useAep5DocumentModal from 'views/customers/hooks/useAep5DocumentModal';
import GeneralStep from './GeneralStep';
import DutyVatBillingStep from './DutyVatBillingStep';

interface Props extends DrawerProps {
    isEditing?: boolean;
    refreshCustomers: Function;
    handleCreateError?: (err: any) => void;
    handleEditError?: (err: any) => void;
    customer?: Customer;
    handleOk: (newCustomer?: any) => void;
    handleEdit?: (customer?: any) => void;
}

export const VefiricationStatuses = {
    VERIFIED: true,
    NOT_VERIFIED: false,
};

const CreateNewCustomerDrawer = ({
    isEditing = false,
    refreshCustomers,
    handleCreateError,
    handleEditError,
    customer,
    handleOk,
    handleEdit,
    ...rest
}: Props) => {
    const { customerId: tenantId } = useSession();
    const [activeStep, setActiveStep] = useState(0);

    const customerSchema = getCustomerSchema();

    const formik = useFormik<Partial<Customer>>({
        initialValues: {},
        validationSchema: customerSchema,
        validateOnMount: true,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: (values) => createNewCustomerSubmit(values),
    });

    const {
        resetForm,
        setValues,
        errors,
        values,
        validateForm,
        setTouched,
        setFieldError,
        setFieldTouched,
        submitForm,
    } = formik;

    const { handleOnPressButton } = useLoadingButton({ onSubmit: submitForm });

    const { checkShowAep5Modal, aep5Modal } = useAep5DocumentModal({ onUploaded: () => refreshCustomers() });

    const {
        data: newCustomer,
        doRequest: doCreateCustomer,
        isLoading: loadingCreate,
        error: createError,
    } = useRequest(createCustomer);

    const {
        data: editedCustomer,
        doRequest: doEditCustomer,
        isLoading: loadingEdit,
        error: editError,
    } = useRequest(editCustomer);

    const handleSubErrors = (err: ErrorResponse) => {
        err?.subErrors?.forEach((e) => {
            if (
                e.field === 'city' ||
                e.field === 'postCode' ||
                e.field === 'country' ||
                e.field === 'addressLine1' ||
                e.field === 'addressLine2'
            ) {
                setFieldError(`address.${e.field}`, e.message);
                setFieldTouched(`address.${e.field}`, true, false);
            } else {
                setFieldError(e.field, e.message);
            }
        });
    };

    const createNewCustomerSubmit = async (values: Partial<Customer>) => {
        let body = {
            ...values,
            brokerIds: [tenantId],
            contact: combinePhone(values.contact),
            contacts: (values.contacts ?? []).map(combinePhone),
        };

        if (body.address) {
            const { address } = body;
            body.address = removeEmptyStringFromObject(address);
            if (objectIsEmpty(body.address)) {
                delete body['address'];
            }
        }

        if (customer && customer.id) {
            await doEditCustomer(customer.id, body as Partial<Customer>);
        } else {
            await doCreateCustomer(body as Customer);
        }
    };

    const handleOnClose = () => {
        rest.onClose?.(null as unknown as React.MouseEvent<Element, MouseEvent>);
        setActiveStep(0);
    };

    const handleConfirm = async () => {
        const validation = await validateForm();
        if (Object.keys(validation).length > 0) {
            setTouched(setNestedObjectValues(validation, true));

            const hasGeneralErrors = !(await generalCustomerSchema.isValid(values));
            const hasDutyVatBillingErrors = !(await dutyVatBillingCustomerSchema.isValid(values));

            if (activeStep === 0 && !hasGeneralErrors && hasDutyVatBillingErrors) {
                setActiveStep(1);
            } else if (activeStep === 1 && !hasDutyVatBillingErrors && hasGeneralErrors) {
                setActiveStep(0);
            }
        }

        if (objectIsEmpty(validation)) {
            await handleOnPressButton();
        } else {
            console.log(errors);
        }
    };

    const handleStepChange = async (step: number) => {
        if (step === 1) {
            try {
                await generalCustomerSchema.validate(values, { abortEarly: false });
            } catch (err) {
                setTouched(setNestedObjectValues(yupToFormErrors(err), true));
                setActiveStep(0);
                return;
            }
        }

        setActiveStep(step);
    };

    useEffect(() => {
        const { address } = values;

        const checkAddressWithAllFieldsEmpty = (values: Address) => {
            const isNotEmpty = Object.entries(values).filter(([key, value]) => value?.length > 0);
            return isNotEmpty.length === 0;
        };

        if (address && (objectIsEmpty(address) || checkAddressWithAllFieldsEmpty(address))) {
            const newValues = { ...values, address: undefined };
            setValues(newValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    useEffect(() => {
        if (loadingCreate === false && newCustomer !== null) {
            if (!createError) {
                refreshCustomers();
                resetForm();
                handleOk(newCustomer);
                checkShowAep5Modal(newCustomer);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingCreate, newCustomer, createError]);

    useEffect(() => {
        if (loadingEdit === false && editedCustomer !== null && handleEdit) {
            if (!editError) {
                refreshCustomers();
                resetForm();
                handleEdit(editedCustomer);
                checkShowAep5Modal(editedCustomer);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingEdit, editedCustomer, editError]);

    useEffect(() => {
        if (createError && handleCreateError) {
            handleCreateError(createError);
            const err: ErrorResponse = { ...createError };
            handleSubErrors(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createError]);

    useEffect(() => {
        if (editError && handleEditError) {
            handleEditError(editError);
            const err: ErrorResponse = { ...editError };
            handleSubErrors(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editError]);

    useEffect(() => {
        const formatContact = (contact: CustomerContact | undefined) => {
            if (!contact) return undefined;
            const phone = extractPhoneNumber(contact.phoneNumber);
            return { ...contact, phoneCountryCode: phone.countryCode || undefined, phoneNumber: phone.phoneNumber };
        };
        const formatCustomer = (customer: Customer | undefined) => {
            if (!customer) return {};
            const contact = formatContact(customer.contact);
            const contacts = (customer.contacts ?? []).map(formatContact);
            return { ...customer, contact, contacts };
        };

        setValues(formatCustomer(customer));
        // Reset touched and error states when customer changes
        setTouched({});
        formik.setErrors({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    useEffect(() => {
        if (!customer) {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Drawer
                title={isEditing ? 'Edit Party' : 'New Party'}
                destroyOnClose
                width={480}
                keyboard={false}
                maskClosable={false}
                onClose={handleOnClose}
                {...rest}
                extra={
                    <Space>
                        <Button onClick={handleOnClose}>Cancel</Button>
                        <Button
                            type="primary"
                            disabled={!formik.dirty}
                            loading={formik.isSubmitting}
                            onClick={handleConfirm}
                        >
                            {isEditing ? 'Update' : 'Create'}
                        </Button>
                    </Space>
                }
            >
                <Steps
                    type="navigation"
                    current={activeStep}
                    items={[{ title: 'General', style: { flex: 0.6 } }, { title: 'Duty, Vat & Billing' }]}
                    onChange={handleStepChange}
                    style={{ marginBottom: 16 }}
                    size="small"
                />

                <FormikProvider value={formik}>
                    <GeneralStep active={activeStep === 0} />
                    <DutyVatBillingStep active={activeStep === 1} customer={customer} />
                </FormikProvider>
            </Drawer>

            {aep5Modal}
        </>
    );
};

export default CreateNewCustomerDrawer;
